import { ModalComponent, ButtonComponent } from "@/components";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { Check, X, CreditCard, Landmark, ShieldCheck } from "lucide-react";
import { WEB_COLORS } from "@/lib/constants";
import BankIllustration from "@/public/icons/BankIlustration";
import UpiMark from "@/public/icons/UpiMark";

export default function CurrentAccountWarningModal({ open, name , onSuccess}) {
  const isBelowMd = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const isBelowSm = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const methods = [
    {
      id: "upi",
      label: "UPI",
      icon: ({ style }) => <UpiMark style={style} />,
    },
    {
      id: "debit",
      label: "Debit Card",
      icon: ({ style }) => <CreditCard style={style} strokeWidth={1.75} />,
    },
    {
      id: "netbanking",
      label: "Net Banking",
      icon: ({ style }) => <Landmark style={style} strokeWidth={1.75} />,
    },
  ];

  return (
    <ModalComponent
      maxWidth={isBelowMd ? "xs" : "md"}
      open={open}
      closeButtonRequired={false}>
      <Box
        sx={{
          px: { xs: 0.5, sm: 2.5 },
          py: { xs: 1.5, sm: 1.5 },
        }}>
        {/* Icon badge */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2,
          }}>
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                height: { xs: 80, sm: 96 },
                width: { xs: 80, sm: 96 },
                borderRadius: 2,
                backgroundColor: WEB_COLORS.paleGreenLight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <BankIllustration
                style={{
                  height: isBelowSm ? 46 : 56,
                  width: isBelowSm ? 46 : 56,
                  color: WEB_COLORS.primary,
                }}
              />
            </Box>
            <Box
              sx={{
                position: "absolute",
                top: -6,
                right: -6,
                height: 32,
                width: 32,
                borderRadius: "9999px",
                backgroundColor: WEB_COLORS.primary,
                border: `3px solid ${WEB_COLORS.white}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
              }}>
              <Check
                style={{
                  height: 16,
                  width: 16,
                  color: WEB_COLORS.white,
                }}
                strokeWidth={3.5}
              />
            </Box>
          </Box>
        </Box>

        {/* Title */}
        <Typography
          component="h2"
          sx={{
            textAlign: "center",
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
            margin: 0,
          }}>
          Update Bank Account Details
        </Typography>
        <Box
          sx={{
            mx: "auto",
            mt: 1.25,
            height: 3,
            width: 36,
            borderRadius: 9999,
          }}
        />

        {/* Rejection callout - red */}
        <Box
          sx={{
            mt: 2.5,
            borderRadius: 1.5,
            backgroundColor: `color-mix(in srgb, ${WEB_COLORS.errorColor} 8%, transparent)`,
            border: `1.75px solid color-mix(in srgb, ${WEB_COLORS.errorColor} 25%, transparent)`,
            px: {xs: 1,sm:1.75},
            py: {xs: 1,sm:1.5},
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}>
          <Box
            sx={{
              height: { xs: 20, sm: 28 },
              width: { xs: 20, sm: 28 },
              borderRadius: "9999px",
              border: `1.75px solid ${WEB_COLORS.errorColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.25,
            }}>
            <X
              style={{ height: '100%', width: '80%', color: WEB_COLORS.errorColor }}
              strokeWidth={2.5}
            />
          </Box>
          <Typography
            component="p"
            sx={{
              fontSize: { xs: 13, sm: 13.5 },
              lineHeight: 1.4,
              pt: 0.25,
              margin: 0,
            }}>
            Mandate rejected: Current Account is not supported. Please enter a{" "}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: WEB_COLORS.errorColor }}>
              Savings Account
            </Box>{" "}
            to continue.
          </Typography>
        </Box>

        {/* Success callout - green outlined */}
        <Box
          sx={{
            mt: 1.5,
            borderRadius: 1.5,
            backgroundColor:
              "color-mix(in srgb, " +
              WEB_COLORS.paleGreenLight +
              " 40%, transparent)",
            border:
              "1.75px solid color-mix(in srgb, " +
              WEB_COLORS.primary +
              " 35%, transparent)",
                px: {xs: 1,sm:1.75},
            py: {xs: 1,sm:1.5},
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}>
          <Box
            sx={{
              height: { xs: 20, sm: 28 },
              width: { xs: 20, sm: 28 },
              borderRadius: "9999px",
              border: "1.75px solid " + WEB_COLORS.primary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}>
            <Check
              style={{ height: "100%", width: "80%", color: WEB_COLORS.primary }}
              strokeWidth={3}
            />
          </Box>
          <Typography
            component="p"
            sx={{
              fontSize: { xs: 13.5, sm: 14 },
              fontWeight: 700,
              color: WEB_COLORS.primary,
              lineHeight: 1.4,
              margin: 0,
            }}>
            Only Savings Accounts are supported.
          </Typography>
        </Box>

        {/* Body copy */}
        <Typography
          component="p"
          sx={{
            mt: 2,
            fontSize: { xs: 13, sm: 13.5 },
            lineHeight: 1.6,
          }}>
          Ensure{" "}
          <Box
            component="span"
            sx={{ fontWeight: 700, color: WEB_COLORS.primary }}>
            UPI
          </Box>
          ,{" "}
          <Box
            component="span"
            sx={{ fontWeight: 700, color: WEB_COLORS.primary }}>
            Debit Card
          </Box>
          , or{" "}
          <Box
            component="span"
            sx={{ fontWeight: 700, color: WEB_COLORS.primary }}>
            Net Banking
          </Box>{" "}
          is enabled on your account to register the mandate.
        </Typography>

        {/* Method tiles - grouped card with dividers */}
        <Box
          sx={{
            mt: 2,
            borderRadius: 2,
            backgroundColor:
              "color-mix(in srgb, " +
              WEB_COLORS.paleGreenLight +
              " 30%, transparent)",
            border: `1px solid ${WEB_COLORS.fadeColor2}50`,
            p: 1,
          }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
            }}>
            {methods.map((m, idx) => {
              return (
                <Box
                  key={m.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    pointerEvents: "none",
                    gap: 1,
                    pt: 1.5,
                    pb: 1.5,
                    px: { xs: 0.5, sm: 0.5 },
                    borderLeft:
                      idx !== 0
                        ? "1px solid " + WEB_COLORS.fadeColor2 + "50"
                        : "none",
                    background: "none",
                    borderTop: "none",
                    borderRight: "none",
                    borderBottom: "none",
                    cursor: "pointer",
                    transition: "transform 0.15s ease",
                  }}>
                  <Box
                    sx={{
                      height: { xs: 44, sm: 56 },
                      width: { xs: 44, sm: 56 },
                      borderRadius: "9999px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s ease",
                      backgroundColor: WEB_COLORS.paleGreenLight,
                      color: WEB_COLORS.primary,
                      transform: "scale(1)",
                    }}>
                    <m.icon
                      style={{ height: isBelowSm ? 20 : 24, width: isBelowSm ? 20 : 24 }}
                    />
                  </Box>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { xs: 11.5, sm: 13 },
                      transition: "color 0.15s ease",
                      color: WEB_COLORS.primary,
                      fontWeight: 500,
                      textAlign: "center",
                    }}>
                    {m.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Ownership note */}
        <Box
          sx={{
            mt: 2,
            mb: 2,
            borderRadius: 1.5,
            backgroundColor:
              "color-mix(in srgb, " +
              WEB_COLORS.paleGreenLight +
              " 40%, transparent)",
            border: `1px solid ${WEB_COLORS.fadeColor2}50`,
            px: 1.75,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}>
          <ShieldCheck
            style={{
              height: 24,
              width: 24,
              color: WEB_COLORS.primary,
              flexShrink: 0,
              marginTop: 2,
            }}
            strokeWidth={2}
          />
          <Typography
            component="p"
            sx={{
              fontSize: { xs: 13, sm: 13.5 },
              lineHeight: 1.4,
              pt: 0.25,
              margin: 0,
            }}>
            Account should be active and associated with{" "}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: WEB_COLORS.primary }}>
              {name}
            </Box>
            .
          </Typography>
        </Box>

        <ButtonComponent type="button" onClick={onSuccess}>Proceed</ButtonComponent>
      </Box>
    </ModalComponent>
  );
}
